import { useEffect, useRef, useState } from "react";
import { Facebook, PlayCircle } from "lucide-react";
import { SITE } from "@/config/site";

export type FacebookPostItem = {
  id: string;
  post_url: string;
  is_video: boolean;
};

function isVideoUrl(url: string, flag: boolean): boolean {
  return flag || /\/(reel|videos|watch)\//i.test(url) || /fb\.watch/i.test(url);
}

function buildEmbedSrc(url: string, isVideo: boolean): string {
  const href = encodeURIComponent(url);
  if (isVideo) {
    return `https://www.facebook.com/plugins/video.php?href=${href}&show_text=false&width=500&t=0`;
  }
  return `https://www.facebook.com/plugins/post.php?href=${href}&show_text=true&width=500`;
}

/**
 * Facade card: paints instantly (no network), then loads the real Facebook
 * iframe only when the user clicks OR when the card itself becomes visible
 * AND the browser is idle. Avoids mounting N iframes simultaneously, which
 * was burning hundreds of ms on the main thread + MBs of FB SDK JS.
 */
function FacebookCard({ post }: { post: FacebookPostItem }) {
  const isVideo = isVideoUrl(post.post_url, post.is_video);
  const ref = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          obs.disconnect();
          const ric = (window as any).requestIdleCallback as
            | ((cb: () => void, opts?: { timeout: number }) => number)
            | undefined;
          if (ric) ric(() => setLoaded(true), { timeout: 2500 });
          else setTimeout(() => setLoaded(true), 400);
        }
      },
      { rootMargin: "100px 0px", threshold: 0.1 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loaded]);

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div
        ref={ref}
        className={isVideo ? "relative w-full" : "w-full"}
        style={isVideo ? { aspectRatio: "16 / 9" } : { height: 640 }}
      >
        {loaded ? (
          <iframe
            src={buildEmbedSrc(post.post_url, isVideo)}
            title="Facebook post"
            className={isVideo ? "absolute inset-0 h-full w-full" : "h-full w-full"}
            style={{ border: "none", overflow: "hidden" }}
            scrolling="no"
            frameBorder={0}
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            loading="lazy"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            aria-label="Load Facebook post"
            className="group relative flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-muted/50 to-muted/20 transition-colors hover:from-muted/70 hover:to-muted/30"
          >
            {isVideo ? (
              <PlayCircle className="h-14 w-14 text-primary/80 transition-transform group-hover:scale-110" />
            ) : (
              <Facebook className="h-12 w-12 text-primary/80 transition-transform group-hover:scale-110" />
            )}
            <span className="text-xs font-medium text-muted-foreground">
              {isVideo ? "Tap to play video" : "Tap to load post"}
            </span>
          </button>
        )}
      </div>
      <div className="border-t border-border bg-muted/30 px-3 py-2 text-center text-xs">
        <a
          href={post.post_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          View on Facebook
        </a>
      </div>
    </div>
  );
}

export function FacebookFeed({ posts }: { posts: FacebookPostItem[] }) {
  if (!posts.length) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        No Facebook posts added yet.{" "}
        <a href={SITE.facebookUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">
          Visit our Facebook page
        </a>
      </div>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {posts.map((p) => (
        <FacebookCard key={p.id} post={p} />
      ))}
    </div>
  );
}
